import { Button, Typography, Box, Grid2 } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ValidatorForm } from "react-material-ui-form-validator";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { accountService } from "../../../_services/accounts.service";
import { StandardList } from "../../../_components/list/standardList.component";
import { StandardListItem } from "../../../_components/list/standardListItem.component";
import { caseInvoiceService } from "../../../_services/caseInvoice.service";
import { getDateFromString, getFormatedDate } from "../../../_helpers/dateTime.helper";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { StandardButton } from "../../../_components/button/primary.button";

export function CaseInvoicesManage() {

    const resetData = () => ({
        invoiceNumber: "",
        paymentDate: ""
    });

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showForm, setShowForm] = useState(false);

    const isAdmin = accountService.isAdmin || accountService.isUser;
    const canCreateManualInvoice = isAdmin && invoices.length === 0;

    useEffect(() => {
        if (id != null) fetchData();
        else {
            setInvoices([]);
            setData(resetData());
        }
    }, [id]);

    function fetchData() {
        if (!id) return;
        setIsLoading(true);
        return caseInvoiceService
            .getAll(id)
            .then(
                (json) => {
                    setInvoices(json);
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function submit(e: any) {
        e.preventDefault();
        setSaving(true);

        const payload = {
            ...data,
            caseId: Number(id),
            invoiceNumber: (data.invoiceNumber ?? "").trim(),
            paymentDate: data.paymentDate || null
        };

        return caseInvoiceService
            .submit(payload)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    setEditingIndex(null);
                    setData(resetData());
                    setShowForm(false);
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => setSaving(false));
    }

    function generateInvoice() {
        setGenerating(true);

        const payload = { caseId: Number(id), invoiceNumber: "" };

        return caseInvoiceService
            .generate(payload)
            .then(
                () => {
                    toastr.success("Invoice generated successfully");
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => setGenerating(false));
    }

    const edit = (index: number) => {
        const invoice = invoices[index];
        setData({
            ...invoice,
            paymentDate: invoice.paymentDate != null ? getFormatedDate(invoice.paymentDate) : ""
        });
        setEditingIndex(index);
        setShowForm(true);
    };

    const addNew = () => {
        setData(resetData());
        setEditingIndex(null);
        setShowForm(true);
    };

    const cancel = () => {
        setShowForm(false);
        setData(resetData());
        setEditingIndex(null);
    };

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6"></Typography>
                {!showForm && isAdmin &&
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {canCreateManualInvoice &&
                            <Button variant="contained" onClick={addNew}>
                                Create Manual Invoice
                            </Button>
                        }
                        <Button
                            variant="contained"
                            onClick={generateInvoice}
                            disabled={generating}
                        >
                            {generating ? "Generating..." : "Generate Invoice"}
                        </Button>
                    </Box>
                }
            </Box>

            {showForm && (
                <ValidatorForm onSubmit={submit} noValidate>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <StandardTextValidator
                                key="invoiceNumber"
                                name="invoiceNumber"
                                label="Invoice Number"
                                fullWidth
                                required
                                validators={["required"]}
                                errorMessages={["Invoice number is required"]}
                                value={data.invoiceNumber}
                                onChange={handleChange}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <StandardTextValidator
                                key="paymentDate"
                                name="paymentDate"
                                label="Payment Date"
                                type="date"
                                fullWidth
                                value={data.paymentDate}
                                onChange={handleChange}
                            />
                        </Grid2>
                        {isAdmin &&
                            <Grid2 size={{ xs: 6 }}>
                                <StandardButton
                                    icon="save"
                                    type="submit"
                                    saving={saving}
                                    loading={saving}
                                >
                                    Save
                                </StandardButton>
                            </Grid2>
                        }
                        <Grid2 size={{ xs: isAdmin ? 6 : 12 }}>
                            <StandardButton
                                icon="block"
                                variant="error"
                                onClick={cancel}
                                saving={saving}
                                loading={saving}
                            >
                                Cancel
                            </StandardButton>
                        </Grid2>
                    </Grid2>
                </ValidatorForm>
            )}

            <StandardList isLoading={isLoading}>
                {!isLoading && invoices.map((item: any, index: number) => (
                    <StandardListItem
                        primary={item.invoiceNumber}
                        secondary={"Payment date: " + (item?.paymentDate != null ? getDateFromString(item.paymentDate) : "")}
                        buttons={
                            isAdmin
                                ? [{ icon: "edit", onClick: () => edit(index), variant: "primary" }]
                                : undefined
                        }
                        key={item.id}
                    />
                ))}
            </StandardList>
        </Box>
    );
}
